/**
 * Backend-ready HTTP client for Workspace tools (reminders, notes, daily).
 * Live path: authenticated `/api/workspace/*` → Firestore `user_workspace/{uid}`.
 */

import {
  ApiEnvelope,
  ReminderItem,
  NoteItem,
  DailyGroup,
  AttentionItem,
  HoldInfo,
  StateSuggestion,
  SentryOrExtSignal,
  WorkContextGraph,
  ResourceRelation,
  ManualWorkHub,
  LlmWikiPage,
  LlmWikiPageList,
} from '../../domain/types';
import { IWorkspaceClient, HttpClientOptions } from './types';
import type { LlmWikiNodeMetadataPage, LlmWikiTopologyManifest } from '@zunelo/shared';
import { createHttpTransport, type HttpTransport } from './httpTransport';
import { parseEnvelope } from './parseEnvelope';
import type { WorkspaceBundle } from '@zunelo/shared';

export class HttpWorkspaceClient implements IWorkspaceClient {
  private readonly transport: HttpTransport;

  constructor(options: HttpClientOptions | string = {}) {
    this.transport = createHttpTransport(options);
  }

  private async request<T>(path: string, options?: RequestInit): Promise<ApiEnvelope<T>> {
    const res = await this.transport.fetch(path, options);
    return parseEnvelope<T>(res, undefined, 'WORKSPACE_HTTP_ERROR');
  }

  async getWorkspaceBundle(): Promise<ApiEnvelope<WorkspaceBundle>> {
    return this.request<WorkspaceBundle>('/api/workspace/bootstrap');
  }

  async getReminders(): Promise<ApiEnvelope<ReminderItem[]>> {
    return this.request<ReminderItem[]>('/api/workspace/reminders');
  }

  async saveReminder(reminder: ReminderItem): Promise<ApiEnvelope<ReminderItem>> {
    return this.request<ReminderItem>('/api/workspace/reminders', {
      method: 'POST',
      body: JSON.stringify(reminder),
    });
  }

  async toggleReminder(id: string, done?: boolean): Promise<ApiEnvelope<ReminderItem>> {
    return this.request<ReminderItem>(`/api/workspace/reminders/${encodeURIComponent(id)}/toggle`, {
      method: 'POST',
      body: typeof done === 'boolean' ? JSON.stringify({ done }) : undefined,
    });
  }

  async deleteReminder(id: string): Promise<ApiEnvelope<{ deleted: boolean }>> {
    return this.request<{ deleted: boolean }>(
      `/api/workspace/reminders/${encodeURIComponent(id)}`,
      {
        method: 'DELETE',
      },
    );
  }

  async getNotes(): Promise<ApiEnvelope<NoteItem[]>> {
    return this.request<NoteItem[]>('/api/workspace/notes');
  }

  async saveNote(note: NoteItem): Promise<ApiEnvelope<NoteItem>> {
    return this.request<NoteItem>('/api/workspace/notes', {
      method: 'POST',
      body: JSON.stringify(note),
    });
  }

  async deleteNote(id: string): Promise<ApiEnvelope<{ deleted: boolean }>> {
    return this.request<{ deleted: boolean }>(`/api/workspace/notes/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  }

  async getLlmWiki(): Promise<ApiEnvelope<LlmWikiPage[]>> {
    return this.request<LlmWikiPage[]>('/api/workspace/wiki');
  }
  async getLlmWikiPage(id: string): Promise<ApiEnvelope<LlmWikiPage | null>> {
    return this.request(`/api/workspace/wiki/${encodeURIComponent(id)}`);
  }
  async getLlmWikiPageBatch(cursor = '0', limit = 200): Promise<ApiEnvelope<LlmWikiPageList>> {
    return this.request(
      `/api/workspace/wiki/batch?cursor=${encodeURIComponent(cursor)}&limit=${limit}`,
    );
  }
  async getLlmWikiGraphManifest(): Promise<ApiEnvelope<LlmWikiTopologyManifest>> {
    return this.request('/api/workspace/wiki/graph/manifest');
  }
  async getLlmWikiNodeMetadata(
    cursor = '0',
    limit = 100,
  ): Promise<ApiEnvelope<LlmWikiNodeMetadataPage>> {
    return this.request(
      `/api/workspace/wiki/graph/nodes?cursor=${encodeURIComponent(cursor)}&limit=${limit}`,
    );
  }

  async saveLlmWikiPage(page: LlmWikiPage): Promise<ApiEnvelope<LlmWikiPage>> {
    return this.request<LlmWikiPage>('/api/workspace/wiki', {
      method: 'POST',
      body: JSON.stringify(page),
    });
  }

  async deleteLlmWikiPage(id: string): Promise<ApiEnvelope<{ deleted: boolean }>> {
    return this.request<{ deleted: boolean }>(`/api/workspace/wiki/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  }

  async getDailyGroups(): Promise<ApiEnvelope<DailyGroup[]>> {
    return this.request<DailyGroup[]>('/api/workspace/daily');
  }

  async saveDailyGroup(group: DailyGroup): Promise<ApiEnvelope<DailyGroup>> {
    return this.request<DailyGroup>('/api/workspace/daily/group', {
      method: 'POST',
      body: JSON.stringify(group),
    });
  }

  async sendDaily(channel: string): Promise<ApiEnvelope<{ sent: boolean; sentAt: string }>> {
    return this.request<{ sent: boolean; sentAt: string }>('/api/workspace/daily/send', {
      method: 'POST',
      body: JSON.stringify({ channel }),
    });
  }

  async saveDailyDraft(markdown: string): Promise<ApiEnvelope<{ saved: boolean }>> {
    return this.request<{ saved: boolean }>('/api/workspace/daily/draft', {
      method: 'POST',
      body: JSON.stringify({ markdown }),
    });
  }

  async getDailyDraft(): Promise<ApiEnvelope<string | null>> {
    return this.request<string | null>('/api/workspace/daily/draft');
  }

  async getDailyStatus(): Promise<ApiEnvelope<{ sent: boolean; sentAt: string | null }>> {
    return this.request<{ sent: boolean; sentAt: string | null }>('/api/workspace/daily/status');
  }

  async clearDailySent(): Promise<ApiEnvelope<{ cleared: boolean }>> {
    return this.request<{ cleared: boolean }>('/api/workspace/daily/status/clear', {
      method: 'POST',
    });
  }

  async bootstrapWorkspace(doc: {
    reminders: ReminderItem[];
    notes: NoteItem[];
    dailyGroups: DailyGroup[];
    dailyDraft: string | null;
    dailySentAt?: string | null;
    llmWiki?: LlmWikiPage[];
  }): Promise<ApiEnvelope<unknown>> {
    return this.request('/api/workspace/bootstrap', {
      method: 'POST',
      body: JSON.stringify(doc),
    });
  }

  async getAllUnifiedAttentionItems(): Promise<ApiEnvelope<AttentionItem[]>> {
    return this.request<AttentionItem[]>('/api/workspace/attention');
  }

  async getWorkContextGraph(): Promise<ApiEnvelope<WorkContextGraph>> {
    return this.request<WorkContextGraph>('/api/workspace/work-context');
  }

  async createManualWorkHub(input: { title: string }): Promise<ApiEnvelope<ManualWorkHub>> {
    return this.request<ManualWorkHub>('/api/workspace/work-context/hubs', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  async upsertWorkContextRelation(
    relation: ResourceRelation,
  ): Promise<ApiEnvelope<WorkContextGraph>> {
    return this.request<WorkContextGraph>('/api/workspace/work-context/relations', {
      method: 'POST',
      body: JSON.stringify(relation),
    });
  }

  async askRadarAssistant(input: {
    question: string;
    contexts: Array<{
      key: string;
      title: string;
      summary: string;
      nextStep: string;
      sources: string[];
    }>;
  }): Promise<ApiEnvelope<{ answer: string; provider?: string; model?: string }>> {
    return this.request<{ answer: string; provider?: string; model?: string }>(
      '/api/workspace/ask',
      {
        method: 'POST',
        body: JSON.stringify(input),
      },
    );
  }

  async getHoldData(): Promise<ApiEnvelope<Record<string, HoldInfo>>> {
    return this.request<Record<string, HoldInfo>>('/api/workspace/hold-data');
  }

  async getSuggestions(): Promise<ApiEnvelope<Record<string, StateSuggestion>>> {
    return this.request<Record<string, StateSuggestion>>('/api/workspace/suggestions');
  }

  async getExternalSignals(): Promise<ApiEnvelope<Record<string, SentryOrExtSignal[]>>> {
    return this.request<Record<string, SentryOrExtSignal[]>>('/api/workspace/external-signals');
  }

  async getPrTalkMap(): Promise<
    ApiEnvelope<Record<string, Array<{ ch: string; text: string; when: string; id: string }>>>
  > {
    return this.request<
      Record<string, Array<{ ch: string; text: string; when: string; id: string }>>
    >('/api/workspace/pr-talk-map');
  }
}
